import { z } from 'zod';
import { decode } from './zodDecoder';

const AccountC = z.object({
    id: z.string(),
    name: z.string(),
    legal_address: z.object({
        line_1: z.string().nullish(),
        line_2: z.string().nullish(),
        line_3: z.string().nullish(),
        city: z.string().nullish(),
        postal_code: z.string().nullish(),
        country_code: z.string(),
    }),
    tenant: z.string(),
    registration_channel: z.string().nullish(),
    life_cycle_state: z.string().nullish(),
    tax_id: z
        .object({
            value: z.string(),
            tax_type: z.string(),
        })
        .nullish(),
    account_type: z.string().nullish(),
    external_identifiers: z
        .array(
            z.object({
                value: z.string(),
                type: z.string(),
            })
        )
        .nullish(),
    _embedded: z
        .object({
            life_cycle_state_information: z
                .object({
                    locked_because: z.string(),
                    locked_notes: z.string().nullish(),
                    locked_at: z.string().nullish(),
                })
                .nullish(),
        })
        .nullish(),
    tenant_specific_data: z
        .object({
            // latam specific data
            city_id: z.string(),
            neighbourhood: z.string(),
            phone_number: z.string(),
            state: z.string(),
            address_details: z.string().nullish(),
        })
        .nullish(),
    contacts: z
        .object({
            general: z.object({
                email: z.string(),
            }),
            billing: z
                .object({
                    email: z.string(),
                })
                .nullish(),
        })
        .nullish(),
    logistics_tenant: z.string().nullish(),
    language: z.string().nullish(),
});
export type AccountRest = z.infer<typeof AccountC>;

const ZalandoRestErrorC = z.object({
    title: z.string(),
    status: z.number(),
    detail: z.string(),
    field_errors: z.record(z.string(), z.string()).optional(),
});

export type ZalandoRestError = z.infer<typeof ZalandoRestErrorC>;
export const decodeZalandoRestError = (parsedObject: unknown): ZalandoRestError =>
    decode(parsedObject, ZalandoRestErrorC);

const AccountsApiResponseC = z.object({
    items: z.array(AccountC),
    // _links: t.type({
    //     next: t.union([t.type({ href: t.string }), t.undefined]),
    //     self: t.union([t.type({ href: t.string }), t.undefined]),
    // }),
});

export type AccountsApiResponse = z.infer<typeof AccountsApiResponseC>;

export const decodeAccountsApiResponse = (parsedObject: unknown): AccountsApiResponse =>
    decode(parsedObject, AccountsApiResponseC);

export const decodeAccount = (parsedObject: unknown): AccountRest => decode(parsedObject, AccountC);

export interface AccountInTable {
    id: string;
    name: string;
    tenant: string;
}

export interface LatamSpecificData {
    cityId: string;
    neighbourhood: string;
    phoneNumber: string;
    state: string;
    addressDetails?: string;
}

export const checkIsLatamSpecificData = (obj: unknown): obj is LatamSpecificData =>
    obj !== undefined &&
    obj !== null &&
    typeof obj === 'object' &&
    'cityId' in obj &&
    typeof obj.cityId === 'string' &&
    obj.cityId.length > 0 &&
    'neighbourhood' in obj &&
    'phoneNumber' in obj &&
    'state' in obj;

export const validateLatamSpecificData = (obj: unknown): LatamSpecificData => {
    if (checkIsLatamSpecificData(obj)) {
        return obj;
    }
    throw new Error('unknown tenant specific data');
};

export interface Account {
    id: string;
    name: string;
    tenant: string;
    registrationChannel?: string;
    accountType?: string;
    // preserveSeparatorsOnPut only relevant as long as we only have accounts with conflicting tax ids in the database.
    // e.g. CNPJ 11.111.111/1111-00 and 11111111111100. Once this is fixed, we can remove preserveSeparatorsOnPut
    // handling
    taxId?: { value: string; taxType: string; preserveSeparatorsOnPut?: boolean };
    legalAddress: {
        line1?: string;
        line2?: string;
        line3?: string;
        postCode?: string;
        city?: string;
        countryCode: string;
    };
    lifeCycleState: LifeCycleState;
    lifeCycleStateInformation?: LifeCycleStateInformation;
    remainingExternalIdentifiers?: ExternalIdentifier[];
    dunsNumbers: string[];
    language?: string;
    tenantSpecificData?: LatamSpecificData;
    contacts?: Contacts;
    logisticsTenant?: string;
}

export type Contacts = {
    general?: {
        email?: string;
    };
    billing?: {
        email?: string;
    };
};

const AccountType = {
    SUPPLIER: 'SUPPLIER',
    BUYER: 'BUYER',
    CARRIER: 'CARRIER',
    PARTNER_CARRIER: 'PARTNER_CARRIER',
    LOGISTICS_BUYER: 'LOGISTICS_BUYER',
} as const;
type AccountType = (typeof AccountType)[keyof typeof AccountType];

const LifeCycleState = {
    active: 'active',
    locked: 'locked',
    created: 'created',
    unknown: 'unknown',
} as const;
type LifeCycleState = (typeof LifeCycleState)[keyof typeof LifeCycleState];

const SearchIdentifier = {
    NAME: 'name',
    VAT: 'vat',
    DUNS: 'duns',
    CPF: 'cpf',
    CNPJ: 'cnpj',
    SCANIA_ID: 'scania_supplier_id',
} as const;
type SearchIdentifier = (typeof SearchIdentifier)[keyof typeof SearchIdentifier];

export { AccountType, LifeCycleState, SearchIdentifier };

export type LifeCycleStateInformation = {
    lockedBecause: string;
    lockedNotes?: string;
    lockedAt?: string;
};

export type ExternalIdentifier = {
    type: string;
    value: string;
};

const taxIdSeparatorsPattern = /[.\-/]/;

const formatTaxId = (value: string, taxType: string): { value: string; preserveSeparatorsOnPut?: boolean } => {
    switch (taxType.toUpperCase()) {
        case 'CPF':
            if (taxIdSeparatorsPattern.test(value)) {
                return { value, preserveSeparatorsOnPut: true };
            }
            return value.length === 11
                ? { value: value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4') }
                : { value };
        case 'CNPJ':
            if (taxIdSeparatorsPattern.test(value)) {
                return { value, preserveSeparatorsOnPut: true };
            }
            return value.length === 14
                ? {
                      value: value.replace(
                          /^([A-Za-z0-9]{2})([A-Za-z0-9]{3})([A-Za-z0-9]{3})([A-Za-z0-9]{4})(\d{2})$/,
                          '$1.$2.$3/$4-$5'
                      ),
                  }
                : { value };
        default:
            return { value };
    }
};

const mapTaxIdToAccount = (taxId: NonNullable<AccountRest['tax_id']>): NonNullable<Account['taxId']> => {
    const formattedTaxId = formatTaxId(taxId.value, taxId.tax_type);
    return {
        value: formattedTaxId.value,
        taxType: taxId.tax_type,
        ...(formattedTaxId.preserveSeparatorsOnPut ? { preserveSeparatorsOnPut: true } : {}),
    };
};

const mapToContacts = (restContacts: AccountRest['contacts']): Contacts | undefined => {
    if (restContacts === null || restContacts === undefined) {
        return undefined;
    }
    const contacts: Contacts = { general: { email: restContacts.general.email } };

    if (restContacts.billing) {
        contacts.billing = { email: restContacts.billing.email };
    }

    return contacts;
};

export const mapToAccount = (accountRest: AccountRest): Account => {
    return {
        id: accountRest.id,
        name: accountRest.name,
        legalAddress: {
            line1: accountRest.legal_address.line_1 ?? undefined,
            line2: accountRest.legal_address.line_2 ?? undefined,
            line3: accountRest.legal_address.line_3 ?? undefined,
            city: accountRest.legal_address.city ?? undefined,
            postCode: accountRest.legal_address.postal_code ?? undefined,
            countryCode: accountRest.legal_address.country_code ?? undefined,
        },
        tenant: accountRest.tenant,
        registrationChannel: accountRest.registration_channel ?? undefined,
        lifeCycleState: mapToLifeCycleState(accountRest.life_cycle_state) ?? undefined,
        taxId: accountRest.tax_id ? mapTaxIdToAccount(accountRest.tax_id) : undefined,
        accountType: accountRest.account_type ?? undefined,
        remainingExternalIdentifiers: accountRest.external_identifiers
            ?.filter(el => el.type !== 'DUNS')
            ?.map((external_identifier: { type: string; value: string }) => ({
                type: external_identifier.type,
                value: external_identifier.value,
            })),
        dunsNumbers: accountRest.external_identifiers?.filter(el => el.type === 'DUNS')?.map(it => it.value) ?? [],
        lifeCycleStateInformation: accountRest._embedded?.life_cycle_state_information
            ? {
                  lockedBecause: accountRest._embedded.life_cycle_state_information?.locked_because ?? undefined,
                  lockedNotes: accountRest._embedded.life_cycle_state_information?.locked_notes ?? undefined,
                  lockedAt: accountRest._embedded.life_cycle_state_information?.locked_at ?? undefined,
              }
            : undefined,
        tenantSpecificData: accountRest.tenant_specific_data
            ? {
                  cityId: accountRest.tenant_specific_data.city_id,
                  neighbourhood: accountRest.tenant_specific_data.neighbourhood,
                  phoneNumber: accountRest.tenant_specific_data.phone_number,
                  state: accountRest.tenant_specific_data.state,
                  addressDetails: accountRest.tenant_specific_data.address_details ?? undefined,
              }
            : undefined,
        contacts: mapToContacts(accountRest.contacts),
        logisticsTenant: accountRest.logistics_tenant ?? undefined,
        language: accountRest.language ?? undefined,
    };
};

export const mapToLifeCycleState = (value: string | null | undefined): LifeCycleState => {
    if (Object.values(LifeCycleState).includes(value as LifeCycleState)) {
        return value as LifeCycleState;
    }
    return LifeCycleState.unknown;
};

const lockReason = {
    SANCTION_LIST_HIT_UNDER_REVIEW: 'SANCTION_LIST_HIT_UNDER_REVIEW',
    SANCTION_LIST_HIT_BLOCKED_AND_MARKED_FOR_DELETION: 'SANCTION_LIST_HIT_BLOCKED_AND_MARKED_FOR_DELETION',
    INSOLVENCY: 'INSOLVENCY',
    OVERDUE_BILLS: 'OVERDUE_BILLS',
    WAITING_FOR_CUSTOMER_FEEDBACK: 'WAITING_FOR_CUSTOMER_FEEDBACK',
    VAT_ID_INVALID: 'VAT_ID_INVALID',
    FAKE_ACCOUNT: 'FAKE_ACCOUNT',
    OTHER: 'OTHER',
} as const;

export type LockReason = (typeof lockReason)[keyof typeof lockReason];

export const mapToLockReason = (value: string | null | undefined): LockReason => {
    if (Object.keys(lockReason).includes(value as LockReason)) {
        return value as LockReason;
    } else {
        throw new Error(`Unknown lockReason: ${value}`);
    }
};

const ManEulaStatusC = z.object({
    account_id: z.string(),
    status: z.string(),
    channel: z.string().nullish(),
    eula_version: z.string().nullish(),
    event_timestamp: z.string().nullish(),
    legal_principal: z.record(z.string(), z.string()).nullish(),
    authenticated_subject: z.string().nullish(),
    client_id: z.string().nullish(),
    vin_snapshot: z.array(z.string()).nullish(),
    etag: z.string(), // extracted from header
});

export type ManEulaStatus = z.infer<typeof ManEulaStatusC>;

const manEulaStatusWithData = z.object({
    account_id: z.string(),
    status: z.string(),
    channel: z.string(),
    eula_version: z.string(),
    event_timestamp: z.string(),
    legal_principal: z.record(z.string(), z.string()),
    authenticated_subject: z.string(),
    client_id: z.string(),
    vin_snapshot: z.array(z.string()),
    etag: z.string(), // extracted from header
});

export type ManEulaStatusWithData = z.infer<typeof manEulaStatusWithData>;

export const decodeManEulaApiResponse = (parsedObject: unknown): ManEulaStatus => decode(parsedObject, ManEulaStatusC);
